import React, { lazy, Suspense, useEffect } from 'react';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import ErrorBoundary from 'src/error/components/ErrorBoundary';
import { getIsEffectiveTypeFastOrNotDefined } from 'src/utils/networkInformation';
import { getIsMobile } from 'src/store/view/selectors';
import { getSearchParamsStartDate } from 'src/store/enqueue/selectors';
import styled, { css } from 'styled-components';
import { TEST_ID_PRICE_CHART_CONTAINER } from './constants';
import { fetchPriceChart as fetchPriceChartAction } from '../../store/priceChart/slice';
import {
  isEmptyResponsePriceChartSelector,
  isShowingPriceChartSelector,
  priceChartIsFetchingSelector,
} from '../../store/priceChart/selectors';
import PriceChartPreview from './components/PriceChartPreview';
import { getInitialStartDate } from './utils';

const PriceChart = lazy(() => import(/* webpackChunkName: "PriceChart" */ '.'));

export const LazyPriceChartComponent = (
  props: PropsFromRedux & { className?: string },
): JSX.Element | null => {
  const {
    fetchPriceChart,
    isLoadingGetGroupHotels,
    isFetching,
    isResponseEmpty,
    className,
    startDate,
    isMobile,
  } = props;

  const isNetworkFast = getIsEffectiveTypeFastOrNotDefined();

  useEffect(() => {
    const initialStartDate = getInitialStartDate(startDate);

    if (isNetworkFast && !isLoadingGetGroupHotels && initialStartDate) {
      fetchPriceChart({ startDate: initialStartDate });
    }
  }, [fetchPriceChart, isLoadingGetGroupHotels, isNetworkFast]);

  if (
    !isNetworkFast ||
    (isResponseEmpty && !isFetching && !isLoadingGetGroupHotels)
  ) {
    return null;
  }

  return (
    <PriceChartContainer
      $isMobile={isMobile}
      data-testid={TEST_ID_PRICE_CHART_CONTAINER}
      className={className}
    >
      {!isLoadingGetGroupHotels ? (
        <ErrorBoundary showDialog={false}>
          <Suspense fallback={null}>
            <PriceChart />
          </Suspense>
        </ErrorBoundary>
      ) : null}
      <PriceChartPreview />
    </PriceChartContainer>
  );
};

const PriceChartContainer = styled.div<{ $isMobile?: boolean }>`
  position: relative;
  height: ${({ $isMobile }) => ($isMobile ? 132 : 144)}px;

  background-color: var(--white);
  border-radius: var(--border-radius);

  ${(props) =>
    props.$isMobile &&
    css`
      margin: 0 8px;
    `}
`;

const mapStateToProps = (state) => ({
  searchDate: getSearchParamsStartDate(state),
  isMobile: getIsMobile(state),
  startDate: getSearchParamsStartDate(state) || '',
  isLoadingGetGroupHotels: isShowingPriceChartSelector(state),
  isFetching: priceChartIsFetchingSelector(state),
  isResponseEmpty: isEmptyResponsePriceChartSelector(state),
});

const mapDispatchToProps = {
  fetchPriceChart: fetchPriceChartAction,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const LazyPriceChart = connector(LazyPriceChartComponent);

export default LazyPriceChart;
